import { Alert, Snackbar } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { IForumPostReplyItemProps, ForumPostReplyItem } from '../../components/forums/ForumPostReplyItem/ForumPostReplyItem';
import { addLikeToReply, addReplyToReply, isLiked, removeLikeFromReply } from '../../services/forums';
import { sendSupportTicket } from '../../services/support';
import { ForumReply, WithID } from '../../types/db';
import { useTranslation } from 'react-i18next';
import { useForumReplyReplies } from '../../hooks/forum/useForumReplyReplies';
import { auth } from '../../services/firebase';

/** Pagination for the replies of reply (lower than replies of post, in purpose) */
const REPLIES_PER_PAGE = 2;

/** The maximum depth we can reply to a reply (limiting for the data base). 
 * Once limit reached, reply will be "@ author" of the reply author on the parent reply (see facebook behavior) */
const MAX_RESPONSE_DEPTH = 3;

/** Container which takes care of a single reply */
export const ForumReplyContainer = ({ postId, reply, depth = 0 }: { postId: string, reply: WithID<ForumReply>, depth?: number }) => {
    const { t } = useTranslation();

    const { data: isReplyLiked, isLoading } = useQuery(["forum", "reply", reply.id],
        () => isLiked(postId, reply.id)
    )
    const { data: dataReplies,//the replies of the reply
        isLoading: isLoadingReplies,
        hasPreviousPage,
        fetchPreviousPage, } = useForumReplyReplies(postId, reply.id, REPLIES_PER_PAGE);

    const [reported, setReported] = useState(false);

    const handleReportClick = () => {
        sendSupportTicket({
            type: "report",
            feature: "forum",
            error_message: "reporting",
            document: "reply",
            doc_id: reply.id,
            user_sender: auth.currentUser?.uid || ''
        })
            .then(() => setReported(true))
    }

    const props = useMemo(() => {
        return mapReplyPropsFromData(postId, { ...reply, isLiked: !!isReplyLiked })
    }, [isReplyLiked, postId, reply])

    if (isLoading) {
        return <ForumPostReplyItem loading />
    }

    return <>
        <ForumPostReplyItem
            {...props}
            replies={isLoadingReplies ?
                new Array(REPLIES_PER_PAGE).fill({}).map((_, index) => <ForumPostReplyItem loading key={`reply-loading-${index}`} />)
                :
                dataReplies?.map(reply => <ForumReplyContainer key={`reply-${reply.id}`} postId={postId} reply={reply} depth={depth + 1} />)
            }
            onLoadMoreReplies={hasPreviousPage ? fetchPreviousPage : undefined}
            onReplyClick={depth >= MAX_RESPONSE_DEPTH ? undefined : props.onReplyClick}
            onReportClick={handleReportClick}
        />
        <Snackbar autoHideDuration={3000} open={reported} onClose={() => setReported(false)} >
            <Alert onClose={() => setReported(false)} severity="success" sx={{ width: '100%' }}>
                {t('forum.reply.reportConfirmMessage')}
            </Alert>
        </Snackbar>
    </>
}


const mapReplyPropsFromData = (
    postId: string,
    data: WithID<ForumReply> & { isLiked: boolean },
): IForumPostReplyItemProps => {
    const handleLikesClick = () => {
        if (data.isLiked) {
            removeLikeFromReply(postId, data.id);
        } else {
            addLikeToReply(postId, data.id);
        }
    }
    const handleReply = async (content: string) => {
        addReplyToReply(postId, data.id, content)
    }
    // const handleDeleteClick = () => {
    //     return deleteReply(data.ref);
    // }
    return {
        author: { avatar: data.author.avatar_url || '', displayname: data.author.display_name },
        timestamp: data.timestamp.toDate(),
        content: data.content,
        likeButton: {
            liked: data.isLiked,
            likesCount: data.likes_count,
            onLikeClick: handleLikesClick,
        },
        onReply: handleReply,
        onReplyClick: () => { },
        repliesCount: data.replies_count,
        onReportClick: () => { },
        onEditClick: undefined,//for now not handling edit on reply
        currentUserAvatar: auth.currentUser?.photoURL || '',
    }
}

export const mapToReplyItem = (data: WithID<ForumReply>, onClick: () => void): IForumPostReplyItemProps => {
    return {
        author: { avatar: data.author.avatar_url || '', displayname: data.author.display_name },
        timestamp: data.timestamp.toDate(),
        content: data.content,
        likeButton: {
            likesCount: data.likes_count,
            onLikeClick: onClick,
        },
        onReplyClick: onClick,
        repliesCount: data.replies_count,
        currentUserAvatar: auth.currentUser?.photoURL || '',
    }
}